<template>
  <sweet-modal
    overlay-theme="dark"
    hide-close-button
    ref="add_task"
    enable-mobile-fullscreen
    class="no-padding-content"
    :width="$mq == 'mobile' ? '100%' : '50%'"
    @close="onModalClose"
    @open="onModalOpen"
  >
    <div class="bg-modal-gradient flex justify-between items-center p-6">
      <div class="text-white text-3xl">
        <i class="fa fa-tasks mr-4"></i> Add a task
      </div>
      <div>
        <button
          type="button"
          class="text-white text-2xl hover:text-grey"
          @click="closeModal"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>
    <div class="p-6">
      <form
        @submit.prevent="uploadFileAndPostTask()"
        data-vv-scope="add-task"
        ref="drop_target"
      >
        <div class="w-full text-left my-4">
          <div class="flex justify-between">
            <label class="font-medium"
              >Title <span class="text-red">*</span></label
            >
            <label class="text-red text-sm font-medium">* Required info</label>
          </div>
          <input
            type="text"
            class="w-full h-10 border rounded px-3 mt-1"
            v-model="add_task.title"
            v-validate="'required|max:250'"
            name="task_title"
            tabindex="1"
            :class="{ 'border-red': errors.has('add-task.task_title') }"
          />
          <div
            v-show="errors.has('add-task.task_title')"
            class="text-red text-sm mt-1"
          >
            {{ errors.first("add-task.task_title") }}
          </div>
        </div>

        <div class="w-full text-left my-4">
          <label class="font-medium"
            >Description <span class="text-red">*</span></label
          >
          <textarea
            type="text"
            class="w-full border rounded py-2 px-3 mt-1"
            v-model="add_task.description"
            v-validate="'required'"
            name="description"
            tabindex="1"
            :class="{ 'border-red': errors.has('add-task.description') }"
          ></textarea>
          <div
            v-show="errors.has('add-task.description')"
            class="text-red text-sm mt-1"
          >
            {{ errors.first("add-task.description") }}
          </div>
        </div>

        <div class="w-full text-left my-4">
          <label class="font-medium"
            >Due date <span class="text-red">*</span></label
          >
          <div class="flex mt-2">
            <flat-pickr
              v-if="isModalOpen"
              class="appearance-none w-full h-10 border rounded px-3 text-black rounded-tr-none rounded-br-none"
              :class="{ 'border-red': errors.has('add-task.due_at') }"
              placeholder="Select Date"
              :config="flatpickr_config"
              :events="[
                'on-change',
                'on-close',
                'on-destroy',
                'on-key-down',
                'on-month-change',
                'on-open',
                'on-parse-config',
                'on-ready',
                'on-value-update',
              ]"
              v-validate
              data-vv-rules="required|date_format:DD/MM/YYYY h:mm A"
              data-vv-value-path="due_at"
              data-vv-name="due_at"
              v-model="add_task.due_at"
              name="due_at"
            ></flat-pickr>
            <button
              type="button"
              class="text-white h-10 px-4 rounded-tr rounded-br"
              :class="{
                'bg-red': errors.has('add-task.due_at'),
                'bg-brand': !errors.has('add-task.due_at'),
              }"
              data-toggle
            >
              <i class="fa fa-calendar"></i>
            </button>
          </div>
          <div
            v-show="errors.has('add-task.due_at')"
            class="text-red text-sm mt-1"
          >
            {{ errors.first("add-task.due_at") }}
          </div>
        </div>

        <div class="w-full text-left my-4">
          <label class="font-medium"
            >Assign to <span class="text-red">*</span></label
          >
          <multiselect
            v-model="add_task.user"
            class="rounded mt-2 border"
            :class="{ 'border-red': errors.has('add-task.assign_to') }"
            track-by="id"
            :custom-label="name"
            placeholder="Search a User"
            :options="allUsers"
            :showLabels="false"
            name="assign_to"
            v-validate="'required'"
            open-direction="below"
            :multiple="true"
          >
            <template slot="tag" slot-scope="props">
              <span class="bg-brand-lighter text-grey-dark rounded-lg p-1 m-1">
                <span class="font-semibold px-2"
                  >{{ props.option.first_name }}
                  {{ props.option.last_name }}</span
                >
                <span
                  class="text-brand border-l border-brand-dark px-2"
                  @click="props.remove(props.option)"
                >
                  <i class="fa fa-times"></i>
                </span>
              </span>
            </template>
          </multiselect>
          <div
            v-show="errors.has('add-task.assign_to')"
            class="text-red text-sm mt-1"
          >
            {{ errors.first("add-task.assign_to") }}
          </div>
        </div>

        <div class="my-6">
          <file-upload
            ref="fileUpload"
            v-show="!vault.storage_exceeded"
          ></file-upload>
        </div>

        <div
          v-if="vault.storage_exceeded"
          class="
            bg-red-lightest
            border border-dashed
            w-full
            p-1
            border-red
            text-sm
          "
        >
          Vault Storage Exceeded, uploading is not allowed
        </div>

        <div class="mt-6 flex justify-between items-center">
          <button
            class="bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark"
            type="submit"
          >
            <i class="fa fa-plus mr-2"></i> Add
          </button>
        </div>
      </form>
    </div>
  </sweet-modal>
</template>

<script>
import { mapGetters } from "vuex";
import flatPickr from "vue-flatpickr-component";
import SelectVaultFileModal from "../SelectVaultFileModal.vue";
import FileUpload from "../FileUpload.vue";

export default {
  components: {
    flatPickr,
    SelectVaultFileModal,
    FileUpload,
  },

  props: ["all-users"],

  data() {
    return {
      add_task: {
        title: "",
        description: "",
        due_at: "",
        user: [],
        close: "",
      },

      isModalOpen: false,

      flatpickr_config: {
        wrap: true,
        allowInput: false,
        dateFormat: "d/m/Y h:i K",
        enableTime: true,
        monthSelectorType: "static",
        onChange: (selectedDates) => {
          if (!_.isNil(selectedDates)) {
            this.add_task.due_at = "";
            this.add_task.due_at = moment(selectedDates[0]).format(
              "DD/MM/YYYY h:mm A"
            );
          }
        },
      },
    };
  },

  methods: {
    closeModal() {
      this.$refs.add_task.close();
    },
    uploadFileAndPostTask() {
      this.$validator.validateAll("add-task").then((result) => {
        if (result) {
          const files = this.$refs.fileUpload.getFiles();
          const vaultFiles = this.$refs.fileUpload.getVaultFiles();

          this.$emit("processing", "Posting task, please wait...");

          this.$emit("post-task", {
            task: this.add_task,
            files: files,
            vaultFiles: vaultFiles,
          });
        }
      });
    },
    onModalOpen() {
      this.isModalOpen = true;
      this.resetModalState();
    },
    onModalClose() {
      this.isModalOpen = false;
      this.resetModalState();
    },
    resetModalState() {
      this.add_task = {
        title: "",
        description: "",
        due_at: "",
        user: [],
        close: "",
      };
      this.errors.clear("add-task");

      // fixed assigned to multiselect wont clear
      this.$nextTick(() => {
        this.errors.clear();
        this.$nextTick(() => {
          this.$validator.reset();
        });
      });

      this.$refs.fileUpload.cleanup();
    },
    name(user) {
      if (!user.last_name) {
        return user.first_name;
      }
      return user.first_name + " " + user.last_name;
    },
  },

  computed: {
    ...mapGetters({
      user: "getUser",
      role: "getUserRole",
      vault: "getCurrentVault",
    }),
  },
};
</script>

<style scoped>
.multiselect >>> .multiselect__tags {
  border: none !important;
}

button:focus {
  outline: none;
}
</style>